// Phase 9.3 — Single BOM detail + edit page.
// Route: /tools/fnb-po/bom/:pos_sku

import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function FnbPoBomDetail() {
  const { pos_sku } = useParams();
  const [bom, setBom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [addOptions, setAddOptions] = useState([]);
  const [mappingType, setMappingType] = useState("direct");
  const [ingredients, setIngredients] = useState([]);
  const [isVerified, setIsVerified] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/fnb/bom/${encodeURIComponent(pos_sku)}`);
      setBom(r.data);
      setMappingType(r.data.mapping_type);
      setIngredients(r.data.ingredients || []);
      setIsVerified(!!r.data.is_verified);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setLoading(false);
    }
  }, [pos_sku]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!addSearch || addSearch.length < 2) { setAddOptions([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/fnb/items?search=${encodeURIComponent(addSearch)}&limit=10`);
        if (!cancelled) setAddOptions(r.data.items || []);
      } catch {}
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [addSearch]);

  const addIngredient = (it) => {
    setIngredients((prev) => [...prev, {
      stock_sku: it.sku,
      stock_name: it.name,
      stock_uom: it.uom,
      qty_per_serving: 1.0,
      uom: it.uom || "Pcs",
      notes: null,
    }]);
    setAddSearch("");
    setAddOptions([]);
    // Auto-flip type based on count
    if (ingredients.length + 1 > 1) setMappingType("recipe");
  };

  const removeIngredient = (i) => {
    setIngredients((prev) => prev.filter((_, j) => j !== i));
  };

  const updateIngredient = (i, patch) => {
    setIngredients((prev) => prev.map((ing, j) =>
      j === i ? { ...ing, ...patch } : ing,
    ));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        mapping_type: ingredients.length === 0 ? "unmapped" : mappingType,
        ingredients: ingredients.map((i) => ({
          stock_sku: i.stock_sku,
          qty_per_serving: Number(i.qty_per_serving || 0),
          uom: i.uom || null,
          notes: i.notes || null,
        })),
      };
      const r = await api.patch(`/fnb/bom/${encodeURIComponent(pos_sku)}`, body);
      setBom(r.data);
      toast.success("BOM saved");
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => {
    if (ingredients.length === 0) {
      toast.error("Add at least one ingredient before verifying");
      return;
    }
    try {
      const r = await api.patch(`/fnb/bom/${encodeURIComponent(pos_sku)}`,
        { is_verified: true, ingredients: ingredients.map((i) => ({
          stock_sku: i.stock_sku,
          qty_per_serving: Number(i.qty_per_serving || 0),
          uom: i.uom || null,
          notes: i.notes || null,
        })), mapping_type: ingredients.length === 1 ? "direct" : "recipe" });
      setBom(r.data);
      setIsVerified(true);
      toast.success("BOM verified");
    } catch (e) {
      toast.error(detailFromError(e));
    }
  };

  const softDelete = async () => {
    if (!window.confirm("Reset this BOM to unmapped? Ingredients will be cleared.")) return;
    try {
      const r = await api.delete(`/fnb/bom/${encodeURIComponent(pos_sku)}`);
      setBom(r.data);
      setIngredients([]);
      setMappingType("unmapped");
      setIsVerified(false);
      toast.success("BOM reset to unmapped");
    } catch (e) {
      toast.error(detailFromError(e));
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!bom) return null;

  const stats = bom.sales_stats || {};

  return (
    <div className="space-y-6" data-testid={`bom-detail-${pos_sku}`}>
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="bom-detail-name">
              {bom.pos_item_name}
            </h1>
            {isVerified && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-900">
                ✓ Verified
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground font-mono mt-1">
            POS SKU: <b>{bom.pos_sku}</b>
          </p>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/tools/fnb-po/bom">← All BOMs</Link>
        </Button>
      </header>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        {/* Ingredients */}
        <Card data-testid="ingredients-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Ingredients ({ingredients.length})</div>
              <div className="relative">
                <Input
                  placeholder="Add ingredient — search SKU/name…"
                  value={addSearch}
                  onChange={(e) => setAddSearch(e.target.value)}
                  className="text-xs w-56"
                  data-testid="add-ingredient-search"
                />
                {addOptions.length > 0 && (
                  <div className="absolute right-0 top-full mt-1 w-72 bg-background border border-border/70 rounded-md shadow-md z-10 max-h-64 overflow-auto">
                    {addOptions.map((it) => (
                      <button
                        key={it.sku}
                        className="block w-full text-left px-3 py-1.5 text-xs hover:bg-muted"
                        onClick={() => addIngredient(it)}
                        data-testid={`add-ing-opt-${it.sku}`}
                      >
                        <div className="font-mono text-[10px]">{it.sku}</div>
                        <div>{it.name}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {ingredients.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center" data-testid="ingredients-empty">
                No ingredients yet. Search above to add stock items.
              </div>
            ) : (
              <Table data-testid="ingredients-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Stock item</TableHead>
                    <TableHead className="w-32 text-right">Qty / serving</TableHead>
                    <TableHead className="w-24">UOM</TableHead>
                    <TableHead className="w-16" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ingredients.map((ing, i) => (
                    <TableRow key={`${ing.stock_sku}-${i}`} data-testid={`ing-row-${i}`}>
                      <TableCell>
                        <div className="text-sm">{ing.stock_name || ing.stock_sku}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">
                          {ing.stock_sku}
                          {ing.uom_mismatch && (
                            <span className="ml-2 text-amber-800">⚠ UOM differs from stock ({ing.stock_uom})</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number" min={0} step={0.01}
                          value={ing.qty_per_serving}
                          onChange={(e) => updateIngredient(i, { qty_per_serving: e.target.value })}
                          className="h-7 text-xs text-right w-24"
                          data-testid={`ing-qty-${i}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={ing.uom || ""}
                          onChange={(e) => updateIngredient(i, { uom: e.target.value })}
                          className="h-7 text-xs w-20"
                          data-testid={`ing-uom-${i}`}
                        />
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => removeIngredient(i)}
                          className="text-muted-foreground hover:text-destructive text-lg leading-none"
                          data-testid={`ing-remove-${i}`}
                          aria-label="Remove"
                        >×</button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader><div className="text-sm font-medium">Sales stats</div></CardHeader>
            <CardContent className="text-xs space-y-1">
              <div>Total qty sold: <b>{stats.qty_sold_total?.toLocaleString?.() ?? "—"}</b></div>
              <div>Invoices: <b>{stats.invoice_count?.toLocaleString?.() ?? "—"}</b></div>
              {stats.first_sale && (
                <div>First sale: {String(stats.first_sale).slice(0, 10)}</div>
              )}
              {stats.last_sale && (
                <div>Last sale: {String(stats.last_sale).slice(0, 10)}</div>
              )}
              {(stats.top_locations || []).length > 0 && (
                <div className="pt-2 border-t border-border/40">
                  <div className="text-muted-foreground">Top locations</div>
                  {stats.top_locations.map((l, i) => (
                    <div key={l.location_id} className="flex justify-between">
                      <span className="font-mono text-[10px]">{l.location_id}</span>
                      <span>{Math.round(l.qty).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><div className="text-sm font-medium">Actions</div></CardHeader>
            <CardContent className="space-y-2">
              <Button
                onClick={save} disabled={saving}
                variant="outline" className="w-full"
                data-testid="bom-save-btn"
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
              {!isVerified && (
                <Button
                  onClick={verify}
                  className="w-full"
                  disabled={ingredients.length === 0}
                  data-testid="bom-verify-btn"
                >
                  Verify BOM →
                </Button>
              )}
              <Button
                onClick={softDelete} variant="ghost"
                className="w-full text-muted-foreground"
                data-testid="bom-reset-btn"
              >
                Reset to unmapped
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
